/**
 * Motion tokens.
 *
 * One place for every duration and easing curve in the game loop, so the
 * whole product moves with a single rhythm instead of thirty hand-tuned
 * magic numbers scattered across screens.
 *
 * The tally constants below are not styling — they are *the beat sheet*.
 * The tally is the product (see `.nookli/product/vision.md`: it's "where
 * the laughter happens"), and these numbers are what make a duplicate land
 * as a groan instead of a table cell.
 */

/** Standard UI durations, ms. */
export const DUR = {
  instant: 120,
  fast: 180,
  base: 240,
  slow: 360,
  lazy: 600,
} as const;

/** Easing curves as cubic-bezier arrays (framer-motion `ease` format). */
export const EASE = {
  /** Decisive arrival. Default for anything entering. */
  out: [0.16, 1, 0.3, 1],
  /** Symmetric — use for things that move but don't arrive. */
  inOut: [0.65, 0, 0.35, 1],
  /** Snappy overshoot for stamps and chips. */
  back: [0.34, 1.56, 0.64, 1],
} as const;

/** Spring used for the leaderboard re-sort. Weighty, settles fast. */
export const SPRING_SORT = {
  type: 'spring',
  stiffness: 420,
  damping: 34,
  mass: 0.9,
} as const;

/** Spring used for chips flipping in. */
export const SPRING_CHIP = {
  type: 'spring',
  stiffness: 520,
  damping: 26,
} as const;

/**
 * THE BEAT SHEET — per-category tally reveal.
 *
 * Timeline for one category, from t=0 (all rows hidden):
 *
 *   t=0             category header stamps in, full width: "NAMES"
 *   t=HEADER        row 0 reveals — avatar, name, and THE WORD IN BIG TYPE
 *   t=+CHIP_DELAY   row 0's score chip flips in: green 10 (provisional)
 *   +ROW_CADENCE    row 1 lands. Same beat. Then row 2, row 3…
 *                   Seat order, every category, so you learn to anticipate
 *                   "…and now MY answer."
 *
 *   THE COLLISION — when a row lands whose word matches an earlier row, it
 *   shows green 10 for COLLISION_HOLD, and then BOTH chips flip 10 → amber 5
 *   together, with a link line drawn between them. You watch your own 10 die
 *   because your brother wrote the same word. The downgrade IS the joke.
 *
 *   dwell           hold. DWELL_CLEAN if nothing interesting happened;
 *                   DWELL_DRAMA if the category had any duplicate or invalid
 *                   — that's when people are actually laughing, so don't
 *                   walk on the laugh.
 *
 * Auto-paced, never tap-paced. The previous stepwise tally died because it
 * made the host tap "Next" four times per round. The host's job is to
 * intervene, not to click through.
 */
export const TALLY = {
  /** Category header alone on screen before the first row lands. */
  HEADER_MS: 800,
  /** Gap between successive rows landing. */
  ROW_CADENCE_MS: 900,
  /** Delay from a row landing to its score chip flipping in. */
  CHIP_DELAY_MS: 300,
  /** How long a doomed 10 gets to live before the downgrade. */
  COLLISION_HOLD_MS: 250,
  /** Dwell when the category was clean — nothing to laugh at. */
  DWELL_CLEAN_MS: 3000,
  /** Dwell when there was a duplicate or an invalid. Let it breathe. */
  DWELL_DRAMA_MS: 6000,

  /** "Pencils down." The collective exhale. Do not decorate it. */
  PENCILS_MS: 1200,
  /** The bridge: totals re-sort, then "Next round in 5…". */
  BRIDGE_MS: 5000,

  /** Fixed row geometry — lets us draw collision link lines arithmetically
   *  rather than measuring the DOM (which is fragile mid-animation). */
  ROW_H: 64,
  ROW_GAP: 8,
} as const;

/**
 * STAGE GEOMETRY — the only thing that changes on a desktop.
 *
 * THE RULE: same durations, bigger travel.
 *
 * A 480px wheel decelerating over the same 700ms reads as MORE cinematic
 * than a 280px one, because the arc is longer. Slowing durations down to
 * "fill" a big screen reads as sluggish. So every duration, easing curve
 * and spring in this file is surface-invariant, and the desktop layer is
 * allowed to change exactly one thing: how far things move.
 *
 * The tally beat sheet above is NOT retuned per surface. Those timings are
 * the product's comedy timing. Same jokes, bigger stage.
 */
export const STAGE = {
  /** The landing / spin wheel. CSS clamps it: min(480px, 44vh). Phone: 280. */
  WHEEL_PX_LG: 480,
  /** UNCHANGED from the phone — and that is the point. The `layoutId` handoff
   *  from the settled wheel letter lands on a 112px letter on every surface,
   *  so spin → race stays one continuous move instead of a cut plus a jump. */
  LETTER_RACE_LG: 112,
  /** Tally row height. Phone 64. Feeds `rowCenterY`, so the collision link
   *  lines get their longer travel for free. */
  TALLY_ROW_H_LG: 80,
  /** Twice the pieces at ≥1024 — three times the canvas. Still fires ONCE,
   *  at game end, never at round end. */
  CONFETTI_MULT_LG: 2,
} as const;

/** Reduced-motion users get the information, not the theatre. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Vertical centre of row `i` within the rows container.
 *
 * `rowH` defaults to the phone's 64 and is passed 80 on desktop — which is
 * all it takes for the collision link lines to draw correctly against the
 * taller rows. Geometry, not measurement: measuring the DOM mid-animation
 * is exactly the fragility this arithmetic exists to avoid.
 */
export function rowCenterY(i: number, rowH: number = TALLY.ROW_H): number {
  return i * (rowH + TALLY.ROW_GAP) + rowH / 2;
}
